import { IconButton, useMediaQuery } from "@chakra-ui/react"
import { isLikertScaleComplete } from "@pathwright/blocks-core/src/blocks/likertscale/type/completion"
import {
  BLOCK_INPUT_STATES,
  useBlockCompletion,
  useBlocksConfig
} from "@pathwright/blocks-engine"
import { BlockText } from "@pathwright/blocks/src/shared"
import { Content } from "@pathwright/ui/src/components/content/Content"
import { useUIThemeContext } from "@pathwright/ui/src/components/ui/PathwrightUI"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import {
  type ComponentType,
  type CSSProperties,
  type ReactNode,
  useEffect,
  useMemo,
  useState
} from "react"
import QuestionHeader from "../Question/QuestionHeader"
import { QuestionContainer } from "../Question/ui"
import type { ScaleProps } from "./scale"
import { MobileScaleSelection } from "./scale"
import { DesktopStatement, MobileStatement } from "./statement"
import type { LikertScaleBlockProps, LikertScaleStatement } from "./types"
import { LIKERT_MOBILE_BREAKPOINT, StatementListContainer } from "./ui"
import {
  getAnsweredStatementCount,
  getLikertResponseMap,
  getLikertScale,
  getLikertStatements,
  updateLikertResponse
} from "./utils"

const BlocksContentContainer = Content.Container as ComponentType<{
  children: ReactNode
  style?: CSSProperties
  flexForPrint?: boolean
  className?: string
}>
const LikertQuestionContainer = QuestionContainer as ComponentType<{
  children: ReactNode
  blockStyles?: LikertScaleBlockProps["blockStyles"]
}>
const LikertQuestionHeader = QuestionHeader as ComponentType<{
  data: LikertScaleBlockProps["data"]
  onChange?: LikertScaleBlockProps["onChange"]
  questionField?: string
  preview?: boolean
}>
const LikertScaleView = ({
  data,
  onChange,
  blockStyles,
  id,
  preview
}: LikertScaleBlockProps) => {
  const {
    setUseAutocomplete,
    setBlockFunctions,
    preventChanges,
    getBlurProps
  } = useBlockCompletion()
  const { disableInput, templateVariables } = useBlocksConfig()
  const { primaryBrandColor } = useUIThemeContext()
  const [isMobile] = useMediaQuery([
    `(max-width: ${LIKERT_MOBILE_BREAKPOINT}px)`
  ])
  const [expandedAnsweredStatementID, setExpandedAnsweredStatementID] =
    useState<string | null>(null)
  const showNumbers = data.showNumbers !== false

  const scale = useMemo(() => getLikertScale(data.scale), [data.scale])
  const statements = useMemo(
    () => getLikertStatements(data.statements),
    [data.statements]
  )
  const answersByStatement = useMemo(
    () => getLikertResponseMap(data),
    [data.answer?.responses, data.statements, data.scale]
  )
  const optionsByID = useMemo(
    () => new Map(scale.map((option) => [option.scaleOptionID, option])),
    [scale]
  )

  useEffect(() => {
    setUseAutocomplete(true)
    setBlockFunctions({
      validateInput,
      getBlockIsCompleted: isLikertScaleComplete,
      deferInvalidUntilBlur: true
    })
  }, [])

  const validateInput = (nextData: Record<string, any> = {}) => {
    const answeredStatements = getAnsweredStatementCount(nextData)
    if (!answeredStatements) return BLOCK_INPUT_STATES.NONE
    return isLikertScaleComplete(nextData)
      ? BLOCK_INPUT_STATES.VALID
      : BLOCK_INPUT_STATES.INVALID
  }

  const handleSelect = (statementID: string, selectedScaleOptionID: string) => {
    const answer = updateLikertResponse({
      data,
      statementID,
      selectedScaleOptionID
    })
    setExpandedAnsweredStatementID(null)
    onChange({ answer }, true)
  }

  const toggleAnsweredStatement = (statementID: string) => {
    if (!answersByStatement.has(statementID)) return
    setExpandedAnsweredStatementID((current) =>
      current === statementID ? null : statementID
    )
  }

  const disableChoices = Boolean(disableInput || preventChanges)

  const getScaleProps = (
    statementID: string
  ): Omit<ScaleProps, "statementLabel"> => ({
    scale,
    showNumbers,
    name: `likert-${id}-${statementID}`,
    selectedScaleOptionID: answersByStatement.get(statementID),
    checkedColor: primaryBrandColor,
    disabled: disableChoices,
    onSelect: (scaleOptionID) => handleSelect(statementID, scaleOptionID)
  })

  const renderDesktopStatement = (statement: LikertScaleStatement) => {
    return (
      <DesktopStatement
        key={statement.statementID}
        statementID={statement.statementID}
        statementLabel={statement.label}
        scaleProps={getScaleProps(statement.statementID)}
      >
        <BlockText html={statement.label} templateContext={templateVariables} />
      </DesktopStatement>
    )
  }

  const renderMobileStatement = (statement: LikertScaleStatement) => {
    const selectedScaleOptionID = answersByStatement.get(statement.statementID)
    const selectedOption = optionsByID.get(selectedScaleOptionID || "")
    const answered = Boolean(selectedOption)
    const expanded =
      !answered || expandedAnsweredStatementID === statement.statementID

    return (
      <MobileStatement
        key={statement.statementID}
        statementID={statement.statementID}
        statementLabel={statement.label}
        clickable={answered}
        expanded={expanded}
        trailing={
          answered ? (
            <IconButton
              aria-label={expanded ? "Collapse answer" : "Expand answer"}
              size="2xs"
              variant="ghost"
              onClick={(event) => {
                event.stopPropagation()
                toggleAnsweredStatement(statement.statementID)
              }}
            >
              <Pathicon icon={expanded ? "chevron-up" : "chevron-down"} />
            </IconButton>
          ) : null
        }
        // Allow opening the card from the body when collapsed
        onCardClick={
          answered && !expanded
            ? () => toggleAnsweredStatement(statement.statementID)
            : undefined
        }
        onHeaderClick={() => toggleAnsweredStatement(statement.statementID)}
        scaleProps={getScaleProps(statement.statementID)}
        collapsedSummary={
          !expanded && selectedOption ? (
            <MobileScaleSelection
              statementLabel={statement.label}
              scale={scale}
              selectedScaleOptionID={selectedScaleOptionID!}
              showNumbers={showNumbers}
              checkedColor={primaryBrandColor}
            />
          ) : null
        }
      >
        <BlockText html={statement.label} templateContext={templateVariables} />
      </MobileStatement>
    )
  }

  const useMobileLayout = isMobile && !preview
  const renderStatement = useMobileLayout
    ? renderMobileStatement
    : renderDesktopStatement

  return (
    <BlocksContentContainer
      style={{ maxHeight: "100%" }}
      flexForPrint={true}
      className="LikertScaleView"
    >
      <LikertQuestionContainer blockStyles={blockStyles}>
        <div {...getBlurProps()}>
          <LikertQuestionHeader data={data} questionField="prompt" />
          <StatementListContainer $mobile={useMobileLayout}>
            {statements.map(renderStatement)}
          </StatementListContainer>
        </div>
      </LikertQuestionContainer>
    </BlocksContentContainer>
  )
}

export default LikertScaleView
